'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RiCloseCircleFill } from 'react-icons/ri';
import { FiLock, FiUsers } from 'react-icons/fi';
import { useRouter } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';

interface PermissionInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  showBackButton: boolean;
}

const PermissionInfoModal = ({
  isOpen,
  onClose,
  showBackButton,
}: PermissionInfoModalProps) => {
  const router = useRouter();
  const { data: session } = useSession();
  const backToDashboard = () => {
    localStorage.removeItem('addRepoValues');
    router.push('/home');
  };

  const handleReAuth = async () => {
    const provider = session?.user?.provider;
    if (!provider) {
      router.push('/home');
      return;
    }
    await signIn(
      provider,
      {
        redirect: true,
        callbackUrl: window.location.pathname + window.location.search,
      },
      { prompt: 'consent' }
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      backToDashboard();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className=" bg-main-light-bg py-8 [&>button]:hidden">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-xl font-bold">
              {showBackButton
                ? 'Repository Ingestion Failed'
                : 'Repository Access Requirements'}
            </DialogTitle>
            <button
              type="button"
              className="cursor-pointer hover:opacity-80"
              onClick={onClose}
              aria-label="Close"
            >
              <RiCloseCircleFill size={24} />
            </button>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <p className="text-base text-ds-text-subtle">
            To ingest a repository, you need the following permissions:
          </p>

          <div className="p-4 border border-ds-border-subtlest rounded-lg">
            <div className="flex items-center mb-3">
              <FiLock size={20} className="mr-2 text-secondary-button-bg" />
              <span className="font-bold text-base">
                For Private Repositories
              </span>
            </div>
            <p className="pl-7">
              • Write access to the repository you want to ingest
            </p>
          </div>

          <div className="p-4 border border-ds-border-subtlest rounded-lg">
            <div className="flex items-center mb-3">
              <FiUsers size={20} className="mr-2 text-secondary-button-bg" />
              <span className="font-bold text-base">
                For Organization Repositories
              </span>
            </div>
            <div className="flex flex-col gap-3 pl-7">
              <p>
                • Organization access must be granted to Entelligence OAuth App
              </p>
              <div className="border-l-4 border-secondary-button-bg pl-3 py-1">
                <p className="text-sm text-ds-text-subtle">
                  You can find this under the &quot;Organization access&quot;
                  section on the GitHub OAuth page. If you don&apos;t have the
                  required permissions, you can request access from your
                  organization administrator.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-3 sm:flex-col">
          <Button
            variant="secondary"
            className="w-full text-sm h-11 hover:-translate-y-px transition-all"
            onClick={handleReAuth}
          >
            {showBackButton ? 'Try Again' : 'Understood, Continue'}
          </Button>
          {showBackButton && (
            <button
              type="button"
              className="rounded-md p-[10px] text-ds-brand-subtle text-sm font-semibold cursor-pointer w-full bg-ds-brand-subtlest"
              onClick={backToDashboard}
            >
              Back to Dashboard
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PermissionInfoModal;
